import Box from '@mui/material/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import Portal from '@material-ui/core/Portal';
import Typography from '@material-ui/core/Typography';
import ExpandMore from '@material-ui/icons/ExpandMore';
import VerifiedIcon from '@mui/icons-material/Verified';
import { isValid } from 'clients/Clients';
import { getMeWishlist } from 'clients/WishlistClient';
import { getCustomerCatalogInitState } from 'clients/CustomerCatalogClient';
import clsx from 'clsx';
import LinkComp from 'components/atoms/LinkComp';
import NewCustomModal from 'components/mocules/NewCustomModal';
import {
  ARROW,
  DEBT,
  DIAMOND_PROFILE,
  GOLD_PROFILE,
  GUIDE_WISH_ITEM,
  HEART,
  HERE,
  PLATINUM_PROFILE,
  PREMIUM_MENU_ICON,
  REWARD_PROFILE,
  SLIVER_PROFILE,
} from 'constants/Images';
import { SVGPrice } from 'constants/SVG';
import { useAuth } from 'context/Auth';
import { useCart } from 'context/Cart/CartContext';
import { useFetchData } from 'hooks/useFetchData';
import useModal from 'hooks/useModal';
import usePremium from 'hooks/usePremium';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import mapDataProductV2, { getSKUErrorMessage } from 'services/convertNewAPIProduct';
import { getDebt } from 'services/UserService';
import { PREMIUM_LANDING_PAGE_URL } from 'sysconfig';
import CatalogMenuIcon from 'components-v2/atoms/CatalogMenuIcon';
import { MY_ACCOUNT, PRODUCT_CATALOG } from 'constants/Paths';
import FormatNumber, { formatCurrency } from 'utils/FormatNumber';
import ImageFallback, { ImageFallbackProductImage, ImageFallbackStatic } from 'utils/ImageFallback';
import { useStore } from 'zustand-lib/storeGlobal';
import ChainStoreSwitcher from 'components-v2/mocules/ChainStoreSwitcher';
import PremiumMembershipBlock from './PremiumMembershipBlock';
import styles from './styles.module.css';

const isMapData = true;

const MenuItemIcon = ({ item, isActive }) => {
  if (item?.url === PRODUCT_CATALOG) return <CatalogMenuIcon color={isActive ? 'var(--primary-color)' : '#333'} />;
  const src = item?.isMembershipMenu ? PREMIUM_MENU_ICON : isActive ? item?.activeIconUrl : item?.iconUrl;
  return <ImageFallback src={src} width="24" height="24" objectFit="contain" />;
};

const OBJECT_IMAGE = {
  LEVEL_SILVER: <ImageFallbackStatic src={SLIVER_PROFILE} width="61" height="28" alt="Bạc" objectFit="contain" />,
  LEVEL_GOLD: <ImageFallbackStatic src={GOLD_PROFILE} width="69" height="28" alt="Vàng" objectFit="contain" />,
  LEVEL_PLATINUM: <ImageFallbackStatic src={PLATINUM_PROFILE} width="91" height="28" alt="Bạch kim" objectFit="contain" />,
  LEVEL_DIAMOND: <ImageFallbackStatic src={DIAMOND_PROFILE} width="101" height="28" alt="Kim cương" objectFit="contain" />,
};
const WishListItem = ({ product, handleClickLink }) => {
  const appSetting = useStore((state) => state.appSetting);
  const errorMessageProduct = getSKUErrorMessage(product, appSetting);

  const hasError = errorMessageProduct || !product.salePrice || product.status === 'STOP_SELLING' || product.salePrice === 0;

  return (
    <LinkComp href={`/product/${product?.slug}`} onClick={handleClickLink} className={styles?.wishListItem} removeStyles>
      <ImageFallbackProductImage
        src={product?.defaultImage}
        width="70"
        height="70"
        alt="Hình ảnh sản phẩm"
        objectFit="contain"
        size="thumbnail"
        quality={100}
      />
      <div className={styles?.infoProduct}>
        <span className={styles?.name}>{product?.name}</span>
        <span className={styles?.volume}>{product?.volume}</span>
        {hasError ? (
          <Typography className={styles.error}>
            {product.requireGPPMessage || errorMessageProduct || product?.errorMessage || 'Đang cập nhật giá'}
          </Typography>
        ) : (
          <SVGPrice displayPriceFormated={product?.displayPriceFormated || 0} size="sm" />
        )}
      </div>
    </LinkComp>
  );
};

export default function ToggleProfile({ point, level, classCustom, listMenu, user, ...restProps }) {
  const router = useRouter();
  const divRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [wishList, setWishList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPoupLogout, toggleLogout] = useModal(false);

  const { data, isLoading, total } = useFetchData(getMeWishlist, open, isMapData);
  const { hasActiveMembership, membershipName } = usePremium();
  const { clearCart } = useCart();
  const { logout } = useAuth();

  // Danh mục thuốc đang bán: chấm đỏ trên nút dropdown + nhấp nháy mục menu đến khi khách vào trang lần đầu
  const [catalogHighlight, setCatalogHighlight] = useState(false);
  useEffect(() => {
    let mounted = true;
    let visited = false;
    try {
      visited = window.localStorage.getItem('customerCatalogVisited') === '1';
    } catch (e) {
      visited = false;
    }
    if (visited || router.pathname === PRODUCT_CATALOG) {
      setCatalogHighlight(false);
      return undefined;
    }
    (async () => {
      const res = await getCustomerCatalogInitState();
      if (!mounted) return;
      const state = res?.data?.[0];
      // API lỗi (chưa deploy / mạng) → chưa biết, vẫn báo đến khi khách vào trang
      const isNew = !isValid(res) || state?.status === 'NOT_INIT';
      setCatalogHighlight(isNew);
      // Đã init (khách từng vào trang ở máy khác) → ghi nhớ để các lần tải sau không gọi lại
      if (isValid(res) && !isNew) {
        try {
          window.localStorage.setItem('customerCatalogVisited', '1');
        } catch (e) {
          // ignore
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [router.pathname]);

  // lazy load: only get debt when open profile
  const [debt, setDebt] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (debt) return;

    (async () => {
      setDebt(await getDebt(user));
    })();
  }, [open]);

  const handleClick = () => {
    setOpen((value) => !value);
  };

  const handleLogout = () => {
    logout(() => {}, { clearAll: true });
    clearCart();
  };

  const handleMouseEnter = (index) => {
    setActiveTab(index);
  };

  const renderWishList = (data) => {
    if (data?.length > 0) {
      return data?.map((item) => <WishListItem key={item?.slug} product={item} handleClickLink={() => setOpen(false)} />);
    }
    return (
      <div className={styles?.emptyContainer}>
        <ImageFallbackStatic src={GUIDE_WISH_ITEM} width="210" height="210" alt="image" objectFit="contain" />
        <div className={styles?.wrapperHereGif}>
          <ImageFallbackStatic src={HERE} width="56" height="42" alt="image" objectFit="contain" />
        </div>
        <span className={styles?.textGuide}>
          Hãy nhấn vào biểu tượng <span className={styles?.boldText}>“Trái tim” trên thẻ sản phẩm</span> để thêm sản phẩm quan tâm bạn nhé
        </span>
      </div>
    );
  };

  const handleScroll = async () => {
    const div = divRef.current;

    if (div.scrollTop + div.clientHeight === div.scrollHeight) {
      if (wishList?.length < total && !loading) {
        setLoading(true);
        try {
          const loadMoreData = await getMeWishlist({ limit: 10, offset: wishList?.length });
          if (isValid(loadMoreData)) {
            setWishList((prevData) => [...prevData, ...mapDataProductV2({ product: loadMoreData?.data })]);
          }
        } catch (error) {
          setLoading(false);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  // Mục gói thành viên chèn ngay sau "Tài khoản của tôi", chỉ cho khách đang có gói
  const menuItems = useMemo(() => {
    const all = listMenu?.items || [];
    if (!all.length) return all;
    // Mục danh mục luôn nằm ngay dưới "Tài khoản của tôi": CMS có thì lấy item CMS (bỏ khỏi vị trí cũ), chưa có thì chèn tạm
    const catalogItem = all.find((x) => x?.url === PRODUCT_CATALOG) || { label: 'Danh mục thuốc đang bán', url: PRODUCT_CATALOG };
    const items = all.filter((x) => x?.url !== PRODUCT_CATALOG);
    const accountIdx = Math.max(
      items.findIndex((x) => x?.url === '/my-account'),
      0,
    );
    const extra = [catalogItem];
    if (hasActiveMembership) extra.push({ label: membershipName, url: PREMIUM_LANDING_PAGE_URL, isMembershipMenu: true });
    return [...items.slice(0, accountIdx + 1), ...extra, ...items.slice(accountIdx + 1)];
  }, [listMenu, hasActiveMembership, membershipName]);

  const handleQuickAccessLoyaltyPoints = () => {
    router.push('/users/loyalty_points');
  };

  useEffect(() => {
    if (data?.length > 0) {
      setWishList(data);
    }
  }, [data]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box {...restProps} className={styles?.profileContainer}>
        <IconButton className={clsx(styles.iconInfo, classCustom)} onClick={handleClick} data-test="btn-toggle-profile">
          <ExpandMore />
          {catalogHighlight && <span className={styles.newDot} aria-hidden="true" />}
        </IconButton>
        {open && (
          <div className={styles?.profilePopup}>
            <div className={styles?.wishListContainer}>
              <span className={styles?.wishListTitle}>
                <span className={styles?.textTitle}>
                  <ImageFallbackStatic src={HEART} width="24" height="24" alt="Trái tim" quantity={100} objectFit="contain" />
                  Sản phẩm quan tâm
                </span>
                <ButtonBase className={styles?.buttonBase} onClick={() => router.push('/user/wishlist')}>
                  Xem thêm <ImageFallbackStatic src={ARROW} width="16" height="16" alt="Mũi tên" quantity={100} objectFit="contain" />
                </ButtonBase>
              </span>
              <div ref={divRef} onScroll={handleScroll} className={clsx(styles?.wishListContent, isLoading && styles?.isLoading)}>
                {isLoading ? (
                  <CircularProgress />
                ) : (
                  <div className={styles?.renderWishList}>
                    {renderWishList(wishList)}
                    {loading && (
                      <div className={styles?.wrapperLoading}>
                        <CircularProgress />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles?.menuListContainer}>
              <PremiumMembershipBlock />
              <div className={styles?.levelContainer} onClick={handleQuickAccessLoyaltyPoints}>
                <div className={styles?.levelIcon}>
                  <span className={styles?.leftContent}>
                    {debt?.isValid ? (
                      <>
                        <span className={styles?.debtContent}>
                          <ImageFallbackStatic src={DEBT} width="20" height="20" alt="Debt" quantity={100} objectFit="contain" /> Hạn Mức
                        </span>
                        <span className={styles?.debtValue}>{formatCurrency(debt.balance)}</span>
                      </>
                    ) : (
                      <>
                        Cấp bậc
                        {OBJECT_IMAGE[level]}
                      </>
                    )}
                  </span>

                  <span className={styles?.customHr} />
                  <span className={styles?.rightContent}>
                    Điểm tích lũy{' '}
                    <span className={styles?.score}>
                      <ImageFallbackStatic src={REWARD_PROFILE} width="24" height="24" alt="Huy chương" objectFit="contain" />
                      {FormatNumber.formatFloatNumber(point)}
                    </span>
                  </span>
                </div>
              </div>
              <ChainStoreSwitcher user={user} onClose={() => setOpen(false)} />
              <div className={styles?.menuContainer}>
                {menuItems?.map((x, index) => (
                  <Fragment key={x.label}>
                    {x?.url !== '/logout' ? (
                      <LinkComp
                        key={x?.url}
                        href={x?.url}
                        target={x?.isMembershipMenu}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseOut={() => setActiveTab(null)}
                        className={clsx(styles?.menuItem, x?.url === PRODUCT_CATALOG && catalogHighlight && styles?.menuItemHighlight)}
                        removeStyles
                      >
                        <MenuItemIcon item={x} isActive={activeTab === index} />
                        <span className={clsx(styles?.textLabel, x?.url === PRODUCT_CATALOG && styles.textLabelNew)}>
                          {x?.label}
                          {x?.url === PRODUCT_CATALOG && <span className={styles.newBadge}>Mới</span>}
                          {x?.url?.trim() === MY_ACCOUNT && user?.isVerify === true && (
                            <VerifiedIcon
                              titleAccess="Đã xác thực"
                              style={{ fontSize: 16, color: '#005C29', marginLeft: 4, verticalAlign: 'text-bottom' }}
                            />
                          )}
                        </span>
                      </LinkComp>
                    ) : (
                      <ButtonBase
                        key={x?.url}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseOut={() => setActiveTab(null)}
                        onClick={toggleLogout}
                        className={styles?.menuItem}
                      >
                        <MenuItemIcon item={x} isActive={activeTab === index} />
                        <span className={styles?.textLabel}>{x?.label}</span>
                      </ButtonBase>
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        <NewCustomModal
          visible={showPoupLogout}
          onClose={toggleLogout}
          title="Xin xác nhận"
          content="Bạn có chắc muốn đăng xuất?"
          btnOk="Có"
          onClickOk={handleLogout}
          btnOnClose="Không"
        />
        {open && (
          <Portal>
            <ButtonBase disableRipple className={styles?.backdrop} onClick={() => setOpen(false)} />
          </Portal>
        )}
      </Box>
    </ClickAwayListener>
  );
}
